<template>
    <div
        v-if="isVisible"
        class="webphone-contact-overlay"
        :class="{ collapsed: isCollapsed, 'd-none': !loading && contacts.length === 0 && hasSearched }"
    >
        <!-- Contact Cards List -->
        <div class="contact-cards-container">
            <!-- Contact Cards -->
            <div
                v-for="(contact, index) in contacts"
                :key="contact._id"
                class="contact-card"
                @click="openContactProfile(contact)"
            >
                <div class="card-content">
                    <div class="profile-avatar">
                        <a-avatar :size="28" :src="getContactAvatar(contact)" :style="{ backgroundColor: '#667eea' }">
                            {{ getContactInitials(contact) }}
                        </a-avatar>
                    </div>
                    <div class="contact-info">
                        <div class="contact-name">{{ getContactName(contact) }}</div>
                        <div class="contact-details">
                            <div class="channel-info" v-if="getChannelInfo(contact)">
                                <i class="material-icons">{{
                                    getPlatformIcon(getChannelInfo(contact).type || contact.platform)
                                }}</i>
                                <span>{{ getChannelInfo(contact).name }}</span>
                            </div>
                            <div class="channel-deleted" v-else-if="contact.channel">
                                <i class="material-icons">error_outline</i>
                                <span>Kanal Silinmiş</span>
                            </div>
                            <div class="platform-badge" v-else>
                                <i class="material-icons">{{ getPlatformIcon(contact.platform) }}</i>
                                <span>{{ getPlatformName(contact.platform) }}</span>
                            </div>
                            <div class="contact-email" v-if="contact.email">
                                <i class="material-icons">email</i>
                                <span>{{ contact.email }}</span>
                            </div>
                            <div class="contact-meta">
                                <div class="contact-owner" v-if="getOwnerInfo(contact)">
                                    <i class="material-icons">person</i>
                                    <span>{{ getOwnerInfo(contact).name }}</span>
                                </div>
                                <div class="contact-date">
                                    <i class="material-icons">schedule</i>
                                    <span>{{ formatDate(contact.createdAt) }}</span>
                                </div>
                            </div>
                        </div>
                    </div>
                    <div class="contact-actions">
                        <div
                            class="action-button"
                            :class="{ disabled: isChannelDeleted(contact) }"
                            @click.stop="startChat(contact)"
                            :title="isChannelDeleted(contact) ? 'Kanal silinmiş - mesaj gönderilemez' : 'Mesaj Gönder'"
                        >
                            <i class="material-icons">{{ isChannelDeleted(contact) ? "block" : "send" }}</i>
                        </div>
                    </div>
                </div>
            </div>

            <!-- No Results - Sadece arama tamamlandığında ve sonuç yoksa göster -->
            <div v-if="!loading && contacts.length === 0 && hasSearched" class="no-results">
                <i class="material-icons">search_off</i>
                <span>Bu numara ile eşleşen kişi bulunamadı</span>
            </div>
        </div>
    </div>
</template>

<script>
import Vue from "vue";
import { mapActions, mapState } from "vuex";
import { CONTACT_API } from "../../../../../api/visitor";
import { generateRandomId } from "../../../../../utils/session";
import log2 from "../../../../../utils/log2";
import SYSTEM_PUBSUB from "../../../../../utils/systemPubSub";
import ChatWatcher from "../../../../../ChatWatcherV2";

const log = log2("WebPhoneContactOverlay");

export default Vue.extend({
    name: "WebPhoneContactOverlay",
    props: {
        phoneNumber: {
            type: String,
            default: null,
        },
        isVisible: {
            type: Boolean,
            default: false,
        },
        isCollapsed: {
            type: Boolean,
            default: false,
        },
    },
    data() {
        return {
            contacts: [],
            loading: false,
            hasSearched: false,
        };
    },
    computed: {
        ...mapState({
            channels: (state) => state.channel.channels,
            users: (state) => state.user.users.data,
            user: (state) => state.auth.user,
        }),
        normalizedPhoneNumber() {
            if (!this.phoneNumber) return null;
            // Telefon numarasını normalize et
            return this.phoneNumber.replace(/[^\d]/g, "");
        },
    },
    methods: {
        ...mapActions({
            addNewWindow: "app/addNewWindow",
        }),

        async searchContacts() {
            if (!this.normalizedPhoneNumber) {
                this.contacts = [];
                this.hasSearched = false;
                this.$emit("contacts-updated", this.contacts);
                return;
            }

            // Arka planda sessizce arama yap - loading state gösterme
            this.loading = true;

            try {
                // Telefon numarası ile filtreleme grupları oluştur
                const filterGroups = {
                    operator: "or",
                    groups: [
                        {
                            id: generateRandomId(),
                            operator: "or",
                            filters: [
                                {
                                    attribute: "phone",
                                    rule: "contains",
                                    value: this.normalizedPhoneNumber,
                                },
                                {
                                    attribute: "whatsappNumber",
                                    rule: "contains",
                                    value: this.normalizedPhoneNumber,
                                },
                                {
                                    attribute: "phone",
                                    rule: "contains",
                                    value: this.phoneNumber,
                                },
                                {
                                    attribute: "whatsappNumber",
                                    rule: "contains",
                                    value: this.phoneNumber,
                                },
                            ],
                        },
                    ],
                };

                const cursor = { offset: 0, limit: 10 };
                const projectionFields = [
                    "_id",
                    "fullname",
                    "phone",
                    "email",
                    "whatsappNumber",
                    "platform",
                    "ref",
                    "channel",
                    "profilePicThumbObj",
                    "instagramInfo",
                    "whatsappInfo",
                    "createdAt",
                    "owner",
                ];

                const response = await CONTACT_API.listContactsByFilterGroups(filterGroups, cursor, projectionFields);

                this.contacts = response.items || [];
                this.hasSearched = true; // Arama tamamlandı
            } catch (error) {
                log.error("Error searching contacts:", error);
                this.contacts = [];
                this.hasSearched = true; // Hata durumunda da arama tamamlandı sayılır
            } finally {
                this.loading = false;
                this.$emit("contacts-updated", this.contacts);
            }
        },

        getContactAvatar(contact) {
            if (contact.platform === "instagram" && contact.instagramInfo?.profile_pic) {
                return contact.instagramInfo.profile_pic;
            }
            if (contact.profilePicThumbObj?.eurl) {
                return contact.profilePicThumbObj.eurl;
            }
            return null;
        },

        getContactInitials(contact) {
            const name = contact.fullname || contact.name || "N/A";
            return name.substr(0, 1).toUpperCase();
        },

        getContactName(contact) {
            return contact.fullname || contact.name || "İsimsiz";
        },

        getChannelInfo(contact) {
            if (!contact.channel) return null;
            const channelId = typeof contact.channel === "object" ? contact.channel._id : contact.channel;
            return this.channels.find((c) => c._id === channelId);
        },

        getOwnerInfo(contact) {
            if (!contact.owner) return null;
            const ownerId = typeof contact.owner === "object" ? contact.owner._id : contact.owner;
            return this.users.find((u) => u._id === ownerId);
        },

        isChannelDeleted(contact) {
            return contact.channel && !this.getChannelInfo(contact);
        },

        formatDate(dateString) {
            if (!dateString) return "";
            const date = new Date(dateString);
            const now = new Date();
            const diffTime = Math.abs(now - date);
            const diffDays = Math.ceil(diffTime / (1000 * 60 * 60 * 24));

            if (diffDays === 1) {
                return "Bugün";
            } else if (diffDays === 2) {
                return "Dün";
            } else if (diffDays <= 7) {
                return `${diffDays - 1} gün önce`;
            } else if (diffDays <= 30) {
                const weeks = Math.ceil(diffDays / 7);
                return `${weeks} hafta önce`;
            } else if (diffDays <= 365) {
                const months = Math.ceil(diffDays / 30);
                return `${months} ay önce`;
            } else {
                const years = Math.ceil(diffDays / 365);
                return `${years} yıl önce`;
            }
        },

        getPlatformIcon(platform) {
            const icons = {
                whatsapp: "whatsapp",
                "whatsapp-business-api": "whatsapp",
                "whatsapp-v1": "whatsapp",
                "whatsapp-v2": "whatsapp",
                "whatsapp-v3": "whatsapp",
                "whatsapp-unipile": "whatsapp",
                "whatsapp-qr": "whatsapp",
                instagram: "camera_alt",
                telegram: "telegram",
                messenger: "messenger",
                email: "email",
                web: "web",
                mobile: "phone_android",
            };
            return icons[platform] || "person";
        },

        getPlatformName(platform) {
            const names = {
                whatsapp: "WhatsApp",
                "whatsapp-business-api": "WhatsApp Business",
                "whatsapp-v1": "WhatsApp",
                "whatsapp-v2": "WhatsApp",
                "whatsapp-v3": "WhatsApp",
                "whatsapp-unipile": "WhatsApp",
                "whatsapp-qr": "WhatsApp",
                instagram: "Instagram",
                telegram: "Telegram",
                messenger: "Messenger",
                email: "E-posta",
                web: "Web",
                mobile: "Mobil",
                supsis: "Supsis",
                "meta-leads": "Meta Leads",
                trendyol: "Trendyol",
                linkedin: "LinkedIn",
                calendar: "Calendar",
                google: "Google",
                bitrix: "Bitrix",
            };
            return names[platform] || "Bilinmeyen";
        },

        openContactProfile(contact) {
            this.addNewWindow({
                type: "show-visitor",
                title: `${contact.fullname || contact.name || "İsimsiz"} - ${contact.phone || contact.whatsappNumber}`,
                payload: {
                    visitorId: contact._id,
                },
                footer: false,
                sliderOptions: {
                    nextAvailable: false,
                    prevAvailable: false,
                },
            });
        },

        startChat(contact) {
            // Kanal silinmişse chat başlatma
            if (this.isChannelDeleted(contact)) {
                this.$message.error("Kanal silinmiş - mesaj gönderilemez");
                return;
            }

            log("Starting chat with contact:", contact);

            // Contact'ın kanalını al
            const channel = this.getChannelInfo(contact);

            if (!channel) {
                this.$message.error("Kanal bilgisi bulunamadı");
                return;
            }

            // Platform'a göre chat başlat - VisitorDetail.vue'den referans alındı
            if (contact.platform === "whatsapp" || channel.type.startsWith("whatsapp")) {
                let whatsappNumber = contact.whatsappNumber || contact.phone;

                // WhatsApp numarası formatını kontrol et
                if (whatsappNumber && !whatsappNumber.includes("@c.us")) {
                    whatsappNumber = whatsappNumber + "@c.us";
                }

                ChatWatcher.startChatImmediately({
                    whatsappNumber: whatsappNumber,
                    whatsappId: contact.whatsappId,
                    channelId: channel._id,
                    channelType: channel.type,
                });
            } else if (contact.platform === "instagram" || channel.type === "instagram") {
                // Instagram için chat başlat
                ChatWatcher.startChatImmediately({
                    instagramId: contact.ref,
                    channelId: channel._id,
                    channelType: channel.type,
                });
            } else if (contact.platform === "messenger" || channel.type === "messenger") {
                // Messenger için chat başlat
                ChatWatcher.startChatImmediately({
                    messengerId: contact.ref,
                    channelId: channel._id,
                    channelType: channel.type,
                });
            } else {
                // Diğer platformlar için genel contact profili aç
                this.openContactProfile(contact);
            }
        },
    },
    watch: {
        phoneNumber: {
            handler(newValue) {
                if (newValue && this.isVisible) {
                    this.searchContacts();
                }
            },
            immediate: true,
        },
        isVisible: {
            handler(newValue) {
                if (newValue && this.phoneNumber) {
                    this.searchContacts();
                }
            },
        },
    },
});
</script>

<style scoped lang="scss">
.webphone-contact-overlay {
    height: 100%;
    display: flex;
    flex-direction: column;
    font-family: -apple-system, BlinkMacSystemFont, "Segoe UI", "Roboto", sans-serif;
    background: none; // Tamamen şeffaf

    &.collapsed {
        display: none;
    }
}

.contact-cards-container {
    flex: 1;
    padding: 8px;
    overflow-y: auto;
    overflow-x: hidden;
    background: none; // Tamamen şeffaf

    /* Custom scrollbar */
    &::-webkit-scrollbar {
        width: 4px;
    }

    &::-webkit-scrollbar-track {
        background: rgba(102, 126, 234, 0.1);
        border-radius: 2px;
    }

    &::-webkit-scrollbar-thumb {
        background: rgba(102, 126, 234, 0.4);
        border-radius: 2px;

        &:hover {
            background: rgba(102, 126, 234, 0.6);
        }
    }
}

.contact-card {
    background: #ffffff;
    border: 1px solid rgba(102, 126, 234, 0.15);
    border-radius: 16px;
    box-shadow: 0 1px 6px rgba(0, 0, 0, 0.06);
    padding: 8px 12px;
    margin-bottom: 6px;
    cursor: pointer;
    transition: all 0.2s ease;
    min-height: 50px;

    &:last-child {
        margin-bottom: 0;
    }

    &:hover {
        transform: translateY(-1px);
        box-shadow: 0 2px 12px rgba(0, 0, 0, 0.1);
        border-color: rgba(102, 126, 234, 0.3);
    }
}

.card-content {
    display: flex;
    align-items: center;
    gap: 10px;
    width: 100%;

    .profile-avatar {
        flex-shrink: 0;

        .ant-avatar {
            border-radius: 50% !important;
        }
    }

    .contact-info {
        flex: 1;
        min-width: 0;

        .contact-name {
            font-weight: 600;
            color: #1a202c;
            font-size: 12px;
            white-space: nowrap;
            overflow: hidden;
            text-overflow: ellipsis;
            margin-bottom: 3px;
            line-height: 1.2;
        }

        .contact-details {
            display: flex;
            flex-direction: column;
            gap: 1px;

            .channel-info,
            .platform-badge,
            .channel-deleted {
                display: flex;
                align-items: center;
                gap: 3px;

                .material-icons {
                    font-size: 10px;
                    color: #667eea;
                }

                span {
                    font-size: 9px;
                    color: #4a5568;
                    font-weight: 500;
                }
            }

            .channel-deleted {
                .material-icons {
                    color: #ef4444;
                }

                span {
                    color: #ef4444;
                }
            }

            .contact-email {
                display: flex;
                align-items: center;
                gap: 3px;

                .material-icons {
                    font-size: 10px;
                    color: #10b981;
                }

                span {
                    font-size: 9px;
                    color: #4a5568;
                    font-weight: 400;
                    white-space: nowrap;
                    overflow: hidden;
                    text-overflow: ellipsis;
                    max-width: 100px;
                }
            }

            .contact-meta {
                display: flex;
                align-items: center;
                gap: 8px;
                margin-top: 1px;

                .contact-owner,
                .contact-date {
                    display: flex;
                    align-items: center;
                    gap: 2px;

                    .material-icons {
                        font-size: 9px;
                        color: #9ca3af;
                    }

                    span {
                        font-size: 8px;
                        color: #9ca3af;
                        font-weight: 400;
                        white-space: nowrap;
                        overflow: hidden;
                        text-overflow: ellipsis;
                        max-width: 60px;
                    }
                }
            }
        }
    }
}

.contact-actions {
    display: flex;
    gap: 4px;
    flex-shrink: 0;

    .action-button {
        width: 24px;
        height: 24px;
        background: rgba(16, 185, 129, 0.1);
        border: 1px solid rgba(16, 185, 129, 0.3);
        border-radius: 12px;
        display: flex;
        align-items: center;
        justify-content: center;
        cursor: pointer;
        transition: all 0.2s ease;

        &:hover:not(.disabled) {
            background: rgba(16, 185, 129, 0.2);
            border-color: rgba(16, 185, 129, 0.5);
            transform: scale(1.05);
        }

        &.disabled {
            background: rgba(239, 68, 68, 0.1);
            border-color: rgba(239, 68, 68, 0.3);
            cursor: not-allowed;
            opacity: 0.7;

            .material-icons {
                color: #ef4444;
            }
        }

        .material-icons {
            font-size: 14px;
            color: #10b981;
        }
    }
}

.no-results {
    display: flex;
    align-items: center;
    gap: 12px;
    padding: 20px;
    justify-content: center;
    color: #718096;

    .material-icons {
        font-size: 24px;
        color: #cbd5e0;
    }

    span {
        font-size: 14px;
    }
}

// Dark mode support
@media (prefers-color-scheme: dark) {
    .contact-card {
        background: rgba(45, 55, 72, 0.95);
        border-color: rgba(102, 126, 234, 0.2);

        &:hover {
            border-color: rgba(102, 126, 234, 0.4);
        }

        .contact-name {
            color: #ffffff !important;
        }

        .contact-details {
            .channel-info,
            .platform-badge {
                span {
                    color: #e2e8f0 !important;
                }
            }

            .channel-deleted {
                span {
                    color: #fca5a5 !important;
                }
            }

            .contact-email {
                span {
                    color: #e2e8f0 !important;
                }
            }

            .contact-meta {
                .contact-owner,
                .contact-date {
                    .material-icons {
                        color: #6b7280 !important;
                    }

                    span {
                        color: #6b7280 !important;
                    }
                }
            }
        }

        .action-button {
            background: rgba(16, 185, 129, 0.2);
            border-color: rgba(16, 185, 129, 0.4);

            &:hover:not(.disabled) {
                background: rgba(16, 185, 129, 0.3);
                border-color: rgba(16, 185, 129, 0.6);
            }

            &.disabled {
                background: rgba(239, 68, 68, 0.2);
                border-color: rgba(239, 68, 68, 0.4);

                .material-icons {
                    color: #fca5a5 !important;
                }
            }

            .material-icons {
                color: #34d399 !important;
            }
        }
    }

    .no-results {
        span {
            color: #e2e8f0 !important;
        }
    }
}
</style>
